import { allCensusDivisions, allStates } from '../partials/_variables.js';
const { createApp } = Vue;

const app = createApp({
	data() {
		return {
			// Routing stuff....
			currentPage: 'home',
			measuring: null,
			scope: null,
			insufficientLocations: {},

			// Current View
			countyName: null,
			stateAbbr: null,
			country: null,

			currentYear: 2030,
			chartInstance: null,
			isChartRendering: false,
			isNavExpanded: false,
			chartErrorMessage: null,
			chartErrorMessages: {
				revenuePerFarm: null,
				returnPerFarm: null,
				debtToAssetRatio: null,
			},
			measurementConfig: {
				weather: {
					avgDailyMaxTemp: {
						relativeCsv: 'avgmaxtemp_relative.csv',
						absoluteCsv: 'avgmaxtemp_absolute.csv',
						colors: 'temperature',
						thermometerSteps: 11,
						unit: '',
					},
					totalPrecipitation: {
						relativeCsv: 'totalprecipitation_relative.csv',
						absoluteCsv: 'totalprecipitation_absolute.csv',
						colors: 'precipitation',
						thermometerSteps: 7,
						unit: '"',
					},
					veryHotDays: {
						relativeCsv: 'veryhotdays_relative.csv',
						absoluteCsv: 'veryhotdays_absolute.csv',
						colors: 'temperature',
						thermometerSteps: 7,
						unit: '',
					},
				},
				'crop-yield': {
					corn: {
						relativeCsv: 'corn_relative.csv',
						absoluteCsv: 'corn_absolute.csv',
						colors: 'crop-yield',
						thermometerSteps: 11,
						unit: '',
					},
					soybeans: {
						relativeCsv: 'soybeans_relative.csv',
						absoluteCsv: 'soybeans_absolute.csv',
						colors: 'crop-yield',
						thermometerSteps: 11,
						unit: '',
					},
					sorghum: {
						relativeCsv: 'sorghum_relative.csv',
						absoluteCsv: 'sorghum_absolute.csv',
						colors: 'crop-yield',
						thermometerSteps: 11,
						unit: '',
					},
					canola: {
						relativeCsv: 'canola_relative.csv',
						absoluteCsv: 'canola_absolute.csv',
						colors: 'crop-yield',
						thermometerSteps: 11,
						unit: '',
					},
					wheat: {
						relativeCsv: 'wheat_relative.csv',
						absoluteCsv: 'wheat_absolute.csv',
						colors: 'crop-yield',
						thermometerSteps: 11,
						unit: '',
					},
					pea: {
						relativeCsv: 'pea_relative.csv',
						absoluteCsv: 'pea_absolute.csv',
						colors: 'crop-yield',
						thermometerSteps: 11,
						unit: '',
					},
				},
				'profit-risk': {
					debtToAssetRatio: {
						dynamicPath: true,
						basePath: 'profit-risk/debtToAssetRatio',
						params: ['size', 'crop', 'risk'],
						fileTemplate: '{metric}_size{size}_{crop}_risk{risk}.csv',
						unit: '$',
					},
					returnPerFarm: {
						dynamicPath: true,
						basePath: 'profit-risk/returnPerFarm',
						params: ['size', 'crop', 'risk'],
						fileTemplate: '{metric}_size{size}_{crop}_risk{risk}.csv',
						unit: '$',
					},
					revenuePerFarm: {
						dynamicPath: true,
						basePath: 'profit-risk/revenuePerFarm',
						params: ['size', 'crop', 'risk'],
						fileTemplate: '{metric}_size{size}_{crop}_risk{risk}.csv',
						unit: '$',
					},
				},
			},

			// This is only used if you use the select...
			selectedCountyInDropdown: '',

			// Temperature Stuff
			heatColors: [
				'#2B2F8A',
				'#69A3CB',
				'#DCF1F7',
				'#E8E8E8',
				'#FEDC85',
				'#F2623B',
				'#9B0022',
			],
			startingTemp: (Math.random() * (2.8 - 1.8) + 1.8).toFixed(2),
			maxTemp: null,
			minTemp: null,
			tempFluctuation: 0.5,

			precipitationColors: [
				'#AB931B',
				'#CBB672',
				'#E3D8BB',
				'#D1D9F5',
				'#9BB7FA',
				'#4793FE',
			],
			startingPrecipitation: 32,
			maxPrecipitation: 50,
			minPrecipitation: 20,
			precipitationFluctuation: 1.8,

			allStates: allStates,
			allCensusDivisions: allCensusDivisions,

			// Crop Yield Stuff
			cropColors: [
				'#863433',
				'#D07A34',
				'#FDCB93',
				'#9DE1D1',
				'#559F90',
				'#336155',
			],
			startingCropYield: (Math.random() * (-0.65 + 1.83) - 1.83).toFixed(2),
			maxCropYield: 5,
			minCropYield: -5,
			cropYieldFluctuation: 0.35,

			// Temperature Data
			thermometerLabels: [],
			countyData: {},

			// Profit & Risk Stuff
			sampleFarms: {
				one: {
					id: 'one',
					name: 'Example farm #1',
					location: 'Butler, NE',
					locationFull: 'Butler County, Nebraska',
					country: 'us',
					crop: 'Corn & soybeans',
					size: 'Medium',
					creditRisk: 'Medium',
					introText: [
						`Farm 1, spanning 2,000 acres in Butler County, Nebraska, represents a medium-sized operation that grows corn and soybeans. In 2024, the farm had medium credit risk measured by a 0.27 debt-to-asset ratio.`,
						`The projections below show that changes in climate conditions in Butler County, Nebraska may decrease the farm’s revenue between now and 2050. The farm may also experience lower net returns as shown in the second graph. Finally, the bottom graph shows that reductions in net returns will increase the farm’s debt-to-asset ratio over time, increasing its credit risk.`,
					],
				},
				two: {
					id: 'two',
					name: 'Example farm #2',
					location: 'Butler, NE',
					locationFull: 'Butler County, Nebraska',
					country: 'us',
					crop: 'Corn, soybeans, and sorghum',
					size: 'Medium',
					creditRisk: 'Medium',
					introText: [
						`Farm 2 is a medium-sized operation in Butler County, Nebraska spanning 2,000 acres and growing corn, soybeans and sorghum. In 2024, Farm 2 also had medium credit risk, measured by a 0.27 debt-to-asset ratio.`,
						`The top graph shows that Farm 2’s corn, soybean and sorghum rotation has lower annual revenue than Farm 1 and may decrease over time. However, the middle graph shows that Farm 2’s corn, soybean and sorghum rotation will have higher net returns than Farm 1 over time, though still negative. Finally, the bottom graph shows that Farm 2’s debt-to-asset ratio will increase at a significantly slower rate than Farm 1.`,
					],
				},
				three: {
					id: 'three',
					name: 'Example farm #3',
					location: 'Butler, NE',
					locationFull: 'Butler County, Nebraska',
					country: 'us',
					crop: 'Corn & soybeans',
					size: 'Medium',
					creditRisk: 'High',
					introText: [
						`Farm 3, spanning 2,000 acres in Butler County, Nebraska, represents a medium-sized operation that grows corn and soybeans. However, Farm 3 is in a weaker credit risk position today, starting with a 0.52 debt-to-asset ratio.`,
						`The first two graphs show the same revenue and slightly lower net return results than Farm 1. The bottom graph shows that Farm 3’s higher debt-to-asset ratio today causes it to reach critical credit risk conditions sooner, putting it at more immediate financial risk due to climate change impacts on crop production and profitability outcomes.`,
					],
				},
			},

			customFarm: {
				id: 'custom',
				name: 'Default custom farm',

				// Location
				country: 'us',
				location: 'Adair, IA',
				usLocation: 'Adair, IA',
				caLocation: 'Division No. 1, SK',

				// Crop
				crop: 'Corn & soybeans',
				usCrop: 'Corn & soybeans',
				caCrop: 'Canola & wheat',

				size: 'Small',
				creditRisk: 'Low',
			},

			currentFarm: {
				id: 'one',
			},
			profitRiskData: {
				debtToAssetRatio: null,
				returnPerFarm: null,
				revenuePerFarm: null,
			},
			metrics: ['debtToAssetRatio', 'returnPerFarm', 'revenuePerFarm'],
			chartInstances: {},
		};
	},
	methods: {
		setYear(newYear) {
			this.currentYear = newYear;
		},
		formatNumber(value) {
			if (value === undefined || value === null || isNaN(value)) {
				return '-';
			}
			return value.toLocaleString('en-US', {
				minimumFractionDigits: 0,
				maximumFractionDigits: 2,
			});
		},

		updateRangeBackground() {
			const rangeInput = document.querySelector(
				'.year-range-holder input[type="range"]'
			);
			if (rangeInput) {
				const value =
					((rangeInput.value - rangeInput.min) /
						(rangeInput.max - rangeInput.min)) *
					100;
				rangeInput.style.setProperty('--value', `${value}%`);
			}
		},
		assignCountyColors() {
			const self = this;
			const svgs = document.querySelectorAll('.map-counties');
			this.updateRangeBackground();

			if (
				self.currentPage === 'home' ||
				self.currentPage === 'profit-risk' ||
				self.currentPage === 'next-steps'
			)
				return;

			if (
				!self.currentPage ||
				!self.measuring ||
				!self.measurementConfig[self.currentPage]?.[self.measuring]
			) {
				console.error(
					`Invalid measurement type: ${self.measuring} for page: ${self.currentPage}`
				);
				return;
			}

			const measurementData =
				self.measurementConfig[self.currentPage][self.measuring];

			const selectedColors =
				measurementData.colors === 'precipitation'
					? self.precipitationColors
					: measurementData.colors === 'crop-yield'
					? self.cropColors
					: self.heatColors;

			const provinceMap = {
				AB: 'Alberta',
				SK: 'Saskatchewan',
			};

			const reverseProvinceMap = Object.fromEntries(
				Object.entries(provinceMap).map(([abbr, full]) => [full, abbr])
			);

			const insufficientDataArray = Array.isArray(
				self.insufficientLocations[self.currentCategory]
			)
				? self.insufficientLocations[self.currentCategory]
				: Array.from(self.insufficientLocations[self.currentCategory] || []);

			const insufficientDataSet = new Set(
				insufficientDataArray.map((location) =>
					location
						.replace(', ca', '')
						.replace(', us', '')
						.replace(/Division No\. (\d+)/, 'Division_No._$1')
						.replace(/, /g, '_')
						.replace(/\s+/g, '_')
				)
			);

			svgs.forEach((svg) => {
				const paths = svg.querySelectorAll('path');

				paths.forEach((path) => {
					let locality = path.getAttribute('data-locality');
					let region = path.getAttribute('data-region');

					if (!locality || !region) {
						const idParts = path.id.split('_');
						region = idParts.pop();
						locality = idParts.join('_');
					}

					if (/^Division_No_\d+$/.test(locality)) {
						locality = locality.replace('Division_No_', 'Division_No._');
					}

					if (path.id === 'OBrien_IA') {
						locality = "O'Brien";
					}

					let lookupKeys = [`${locality}_${region}`];

					if (provinceMap[region]) {
						lookupKeys.push(`${locality}_${provinceMap[region]}`);
					}

					if (reverseProvinceMap[region]) {
						lookupKeys.push(`${locality}_${reverseProvinceMap[region]}`);
					}

					let formattedKey = `${locality}_${region}`
						.replace(/Division No\. (\d+)/, 'Division_No._$1')
						.replace(/, /g, '_')
						.replace(/\s+/g, '_');

					const isInsufficient = insufficientDataSet.has(formattedKey);

					let tempValue;
					for (const key of lookupKeys) {
						if (self.countyData[key] !== undefined) {
							tempValue = self.countyData[key];
							break;
						}
					}

					if (isInsufficient) {
						path.setAttribute('fill', 'url(#CrossHatch)');
						path.setAttribute('data-value', 'null');
						path.setAttribute('data-insufficient', 'yes');
					} else if (
						tempValue === null ||
						tempValue === 'null' ||
						tempValue === 'NA'
					) {
						path.setAttribute('fill', 'url(#CrossHatch)');
						path.setAttribute('data-value', 'null');
						path.setAttribute('data-insufficient', 'yes');
					} else if (tempValue !== undefined) {
						const color = self.getColorForValue(
							tempValue,
							selectedColors,
							self.maxTemp,
							self.minTemp
						);
						path.setAttribute('fill', color);
						path.setAttribute('data-value', tempValue.toFixed(2));
					}

					if (!path.classList.contains('svg-border')) {
						if (svg.closest('.path-is-clickable')) {
							if (
								tempValue &&
								tempValue !== 'null' &&
								tempValue !== 'NA' &&
								tempValue !== undefined &&
								!isInsufficient
							) {
								path.setAttribute('is-clickable', 'yes');
								path.addEventListener('click', self.handlePathClick);
							}
						}
						if (svg.closest('.map-has-tooltips')) {
							path.setAttribute('tooltip', 'yes');
							path.addEventListener('mouseover', self.showTooltip);
							path.addEventListener('mousemove', self.moveTooltip);
							path.addEventListener('mouseout', self.hideTooltip);
						}
					}
				});

				this.isMapsHaveFills = true;
			});
		},
		handlePathClick(event) {
			const self = this;
			const path = event.target;

			if (path.getAttribute('is-clickable') === 'yes') {
				const id = path.id;
				const parts = id.split('_');

				self.stateAbbr = parts.pop();

				let county = parts.join(' ').replace(/_/g, ' ');
				if (county.startsWith('Division No ')) {
					county = county.replace('Division No ', 'Division No. ');
				}

				self.countyName = county;

				const provinceMap = {
					AB: 'Alberta',
					SK: 'Saskatchewan',
				};

				if (provinceMap[self.stateAbbr]) {
					self.country = 'ca';
				} else {
					self.country = 'us';
				}

				const provinceFullName = provinceMap[self.stateAbbr] || self.stateAbbr;
				self.selectedCountyInDropdown = `${self.countyName}, ${provinceFullName}`;

				self.scope = 'county';
				self.selectCounty();
				self.updateURL();
			}
		},
		getNextValue(previousValue, fluctuation, maxValue, minValue) {
			const change = (Math.random() * (fluctuation * 2) - fluctuation).toFixed(
				2
			);
			let newValue = parseFloat(previousValue) + parseFloat(change);
			if (newValue > maxValue) newValue = maxValue;
			if (newValue < minValue) newValue = minValue;
			return newValue.toFixed(2);
		},
		getColorForValue(value, colors, maxValue, minValue) {
			const valueRange = maxValue - minValue;
			const step = valueRange / (colors.length - 1);
			let index = Math.floor((value - minValue) / step);
			if (index < 0) index = 0;
			if (index >= colors.length - 1) index = colors.length - 2;
			const lowerColor = colors[index];
			const upperColor = colors[index + 1];
			const ratio = ((value - minValue) % step) / step;
			return this.interpolateColor(lowerColor, upperColor, ratio);
		},
		interpolateColor(color1, color2, ratio) {
			if (!color1 || !color2) return color1 || color2;
			const hex = (color) => parseInt(color.slice(1), 16);
			const r = (color) => (hex(color) >> 16) & 0xff;
			const g = (color) => (hex(color) >> 8) & 0xff;
			const b = (color) => hex(color) & 0xff;
			const interpolate = (start, end, ratio) =>
				Math.round(start + (end - start) * ratio);
			const r1 = r(color1),
				g1 = g(color1),
				b1 = b(color1);
			const r2 = r(color2),
				g2 = g(color2),
				b2 = b(color2);
			const rFinal = interpolate(r1, r2, ratio);
			const gFinal = interpolate(g1, g2, ratio);
			const bFinal = interpolate(b1, b2, ratio);
			return `#${((1 << 24) + (rFinal << 16) + (gFinal << 8) + bFinal)
				.toString(16)
				.slice(1)}`;
		},
		showTooltip(event) {
			const self = this;
			const svg = event.target.closest('.map-counties');
			const tooltip = document.getElementById('tooltip');
			let amount = event.target.getAttribute('data-value');
			const isInsufficient = event.target.getAttribute('data-insufficient');

			amount = Number(amount);

			if (self.measuring === 'veryHotDays') {
				amount = Math.round(amount);
			} else {
				amount = amount.toFixed(2);
			}
			const stateAbbr = event.target.getAttribute('data-state');
			const countyName = event.target.getAttribute('data-county');
			const formattedAmount = amount > 0 ? `+${amount}` : amount;

			let unit = '';
			if (
				self.currentPage === 'weather' &&
				self.measuring === 'avgDailyMaxTemp'
			) {
				unit = '°F';
			} else if (
				self.currentPage &&
				self.measuring &&
				self.measurementConfig[self.currentPage]?.[self.measuring]
			) {
				unit =
					self.measurementConfig[self.currentPage][self.measuring].unit || '';
			}

			if (isInsufficient) {
				tooltip.innerText = `${countyName}, ${stateAbbr} (insufficient data)`;
			} else if (self.currentPage === 'weather') {
				tooltip.innerText = `${countyName}, ${stateAbbr}${
					formattedAmount ? ` (${formattedAmount}${unit})` : ''
				}`;
			} else if (self.currentPage === 'crop-yield') {
				tooltip.innerText =
					`${countyName}, ${stateAbbr}` +
					(formattedAmount !== null ? ` (${formattedAmount}%)` : '');
			}

			const pathBBox = event.target.getBoundingClientRect();
			const svgBBox = svg.getBoundingClientRect();
			tooltip.style.left = `${
				pathBBox.left + pathBBox.width / 2 - svgBBox.left
			}px`;
			tooltip.style.top = `${
				pathBBox.top - svgBBox.top - tooltip.offsetHeight - 10
			}px`;
			tooltip.classList.add('visible');
		},
		moveTooltip(event) {
			const tooltip = document.getElementById('tooltip');
			const pathBBox = event.target.getBoundingClientRect();
			const svg = event.target.closest('.map-counties');
			const svgBBox = svg.getBoundingClientRect();
			tooltip.style.left = `${
				pathBBox.left + pathBBox.width / 2 - svgBBox.left
			}px`;
			tooltip.style.top = `${
				pathBBox.top - svgBBox.top - tooltip.offsetHeight - 10
			}px`;
		},
		hideTooltip(event) {
			const tooltip = document.getElementById('tooltip');
			if (tooltip && tooltip.classList) {
				tooltip.classList.remove('visible');
			} else {
				console.warn('Tooltip element not found');
			}
		},
		changeMeasurement(newMeasurement) {
			const self = this;
			const previousCountry = self.country;
			self.measuring = newMeasurement;

			if (['corn', 'soybeans', 'sorghum'].includes(newMeasurement)) {
				self.scope = 'us';
				self.country = 'us';

				if (previousCountry === 'ca') {
					self.selectedCountyInDropdown = '';
					self.countyName = '';
					self.stateAbbr = '';
				}
			} else if (['canola', 'wheat', 'pea'].includes(newMeasurement)) {
				self.scope = 'ca';
				self.country = 'ca';

				if (previousCountry === 'us') {
					self.selectedCountyInDropdown = '';
					self.countyName = '';
					self.stateAbbr = '';
				}
			}

			self.updateURL();
			self.initializeRoute();

			setTimeout(() => {
				self.assignCountyColors();
			}, 300);
		},
		changeScope(newScope) {
			const self = this;
			self.scope = newScope;
			self.updateURL();
			self.initializeRoute();
			setTimeout(() => {
				self.assignCountyColors();
			}, 300);
		},
		selectCounty() {
			const self = this;
			self.scope = 'county';
			self.updateURL();
			self.initializeRoute();
			self.hideTooltip();
		},

		selectFarm(farmId) {
			const self = this;

			if (farmId !== 'custom') {
				self.currentFarm = { ...self.sampleFarms[farmId], id: farmId };
			} else {
				self.currentFarm = { ...self.customFarm, id: 'custom' };
			}

			self.fetchProfitRiskData();
		},
		handleFarmSizeLimitations() {
			// If you have a large farm selected, change the size to medium unless your selected state is Nebraska
			const self = this;
			if (self.customFarm.size === 'Large') {
				const customFarmLocation = self.customFarm.location.split(',');
				const state = customFarmLocation.pop().trim();
				if (state !== 'NE') {
					self.customFarm.size = 'Medium';
				}
			}
		},
		changeFarmCountry(newCountry) {
			const self = this;
			self.customFarm.country = newCountry;

			if (newCountry === 'us') {
				self.customFarm.location = self.customFarm.usLocation;
				self.customFarm.crop = self.customFarm.usCrop;
			} else if (newCountry === 'ca') {
				self.customFarm.location = self.customFarm.caLocation;
				self.customFarm.crop = self.customFarm.caCrop;
			}

			self.handleFarmSizeLimitations();

			self.currentFarm = { ...self.customFarm, id: 'custom' };

			self.fetchProfitRiskData().then(() => {
				self.makeAChart();
			});
		},
		changeFarmLocation(farmLocation) {
			const self = this;

			// If your state is either SD or IA and you have a large farm, change the size to medium
			if (self.customFarm.size === 'Large') {
				const customFarmLocation = farmLocation.split(',');
				const state = customFarmLocation.pop().trim();
				if (state === 'SD' || state === 'IA') {
					self.customFarm.size = 'Medium';
				}
			}

			if (self.customFarm.country === 'us') {
				self.customFarm.usLocation = self.customFarm.location;
			} else if (self.customFarm.country === 'ca') {
				self.customFarm.caLocation = self.customFarm.location;
			}

			self.currentFarm = { ...self.customFarm, id: 'custom' };
			self.fetchProfitRiskData().then(() => {
				self.makeAChart();
			});

			self.handleFarmSizeLimitations();
		},
		changeFarmCrop() {
			const self = this;

			if (self.customFarm.country === 'us') {
				self.customFarm.usCrop = self.customFarm.crop;
			} else if (self.customFarm.country === 'ca') {
				self.customFarm.caCrop = self.customFarm.crop;
			}

			self.currentFarm = { ...self.customFarm, id: 'custom' };
			self.fetchProfitRiskData().then(() => {
				self.makeAChart();
			});
		},
		turnSampleFarmToCustom(farmId) {
			const self = this;
			const sourceFarm = self.sampleFarms[farmId];
			self.customFarm.name = sourceFarm.name;
			self.customFarm.country = sourceFarm.country;
			self.customFarm.location = sourceFarm.location;
			self.customFarm.crop = sourceFarm.crop;
			self.customFarm.size = sourceFarm.size;
			self.customFarm.creditRisk = sourceFarm.creditRisk;
			self.currentFarm.id = 'custom';
		},

		changeCropCounty(evt) {},
		changeCounty(evt) {
			const self = this;

			if (
				self.selectedCountyInDropdown &&
				self.selectedCountyInDropdown.includes(',')
			) {
				const parts = self.selectedCountyInDropdown.split(',');
				self.countyName = parts[0]?.trim();
				self.stateAbbr = parts[1]?.trim();
			} else {
				self.countyName = '';
				self.stateAbbr = '';
			}
		},
		changeFarmSize() {
			const self = this;

			self.currentFarm = { ...self.customFarm, id: 'custom' };

			self.fetchProfitRiskData().then(() => {
				self.makeAChart();
			});
		},

		changeFarmRisk() {
			const self = this;

			self.currentFarm = { ...self.customFarm, id: 'custom' };

			self.fetchProfitRiskData().then(() => {
				self.makeAChart();
			});
		},

		selectCountyFromDropdown() {
			const self = this;

			if (
				!self.countyName ||
				!self.stateAbbr ||
				self.countyName === '' ||
				self.stateAbbr === ''
			) {
				if (
					self.selectedCountyInDropdown &&
					self.selectedCountyInDropdown.includes(',')
				) {
					const parts = self.selectedCountyInDropdown.split(',');
					self.countyName = parts[0]?.trim().replace(/No\.?\s/g, 'No. ');
					self.stateAbbr = parts[1]?.trim();

					if (self.stateAbbr === 'Alberta') self.stateAbbr = 'AB';
					if (self.stateAbbr === 'Saskatchewan') self.stateAbbr = 'SK';

					self.selectedCountyInDropdown = `${self.countyName}, ${self.stateAbbr}`;
				}
			}

			if (['AB', 'SK'].includes(self.stateAbbr)) {
				self.country = 'ca';
			} else {
				self.country = 'us';
			}

			if (self.countyName && self.stateAbbr) {
				self.scope = 'county';
				self.selectCounty();
				self.updateURL();
			} else {
				self.countyName = '';
				self.stateAbbr = '';
				self.selectedCountyInDropdown = '';
			}
		},
		changeCountyRightNow() {
			const self = this;
			let tempCounty = '';
			let tempState = '';

			if (
				self.selectedCountyInDropdown &&
				self.selectedCountyInDropdown.includes(',')
			) {
				const parts = self.selectedCountyInDropdown.split(',');
				tempCounty = parts[0]?.trim();
				tempState = parts[1]?.trim();
			}
			if (tempCounty && tempState) {
				self.countyName = tempCounty;
				self.stateAbbr = tempState;
				self.destroyChart();
				self.makeAChart();
			} else {
				self.countyName = '';
				self.stateAbbr = '';
				self.selectedCountyInDropdown = '';
			}
		},
		destroyChart(id) {
			if (id) {
				Chart.getChart(id)?.destroy();
			} else {
				Chart.getChart('myChart')?.destroy();
			}
		},
		makeAChart() {
			const self = this;

			if (self.currentPage === 'profit-risk') {
				// Ensure charts are only generated if they don't already exist
				self.metrics.forEach((metric) => {
					self.generateProfitRiskChart(metric);
				});
			}

			if (self.isChartRendering) {
				// Chart generation is already in progress. Skipping...
				return;
			}

			self.isChartRendering = true;

			setTimeout(() => {
				const ctx = document.getElementById('myChart');
				if (!ctx) {
					//  Chart element not found, skipping chart creation.
					self.isChartRendering = false;
					return;
				}
				const context = ctx.getContext('2d');
				if (!context) {
					console.error('Failed to get 2D context for the chart.');
					self.isChartRendering = false;
					return;
				}

				if (!self.currentPage || !self.measuring) {
					console.error('No valid measurement selected.');
					self.isChartRendering = false;
					return;
				}

				const measurementData =
					self.measurementConfig[self.currentPage]?.[self.measuring];

				if (!measurementData) {
					console.error(`Measurement config not found for: ${self.measuring}`);
					self.isChartRendering = false;
					return;
				}

				self.generateChart(
					self.currentPage,
					self.measuring,
					measurementData.absoluteCsv
				);

				self.isChartRendering = false;
			}, 100);
		},
		generateChart(currentPage, measuring, fileName) {
			const self = this;
			const filePath = `${baseUrl}/assets/public/csv/${currentPage}/${fileName}`;
			const ctx = document.getElementById('myChart');

			if (!ctx) {
				console.error('Chart element not found');
				return;
			}

			self.destroyChart();

			fetch(filePath)
				.then((response) => response.text())
				.then((csvText) => {
					const rows = csvText.split('\n').map((row) => row.split(','));
					const headers = rows[0];

					if (currentPage === 'weather') {
						const normalizedCountyName = (self.countyName || '')
							.replace(/No\.?\s/g, 'No. ')
							.trim();

						const countyRow = rows.find(
							(row) =>
								(row[1]?.trim() === self.countyName?.trim() ||
									row[1]?.trim() === normalizedCountyName) &&
								row[2]?.trim().toLowerCase() ===
									(self.stateAbbr?.toLowerCase() || '')
						);

						if (!countyRow) {
							console.error(
								`County data not found for ${self.countyName}, ${self.stateAbbr}`
							);
							return;
						}

						const dataValues = countyRow.slice(4);

						const isAllValuesInvalid = dataValues.every(
							(val) => val === 'NA' || val === 'null' || parseFloat(val) === 0
						);

						if (isAllValuesInvalid) {
							self.chartErrorMessage = 'Insufficient data for this region';
							return;
						}

						const years = headers
							.slice(4)
							.filter((_, index) => index % 3 === 0)
							.map((header) => header.split('_')[1]);

						let tempValues = countyRow
							.slice(4)
							.map((val) => parseFloat(val))
							.filter((num) => !isNaN(num));

						if (measuring === 'veryHotDays') {
							tempValues = tempValues.map(Math.round);
						}

						let minTemp = Math.min(...tempValues);
						let maxTemp = Math.max(...tempValues);
						const padding = (maxTemp - minTemp) * 0.15;
						minTemp = Math.max(minTemp - padding, minTemp - 1);
						maxTemp = Math.min(maxTemp + padding, maxTemp + 1);

						const maxTemps = [];
						const lowerBounds = [];
						const upperBounds = [];

						for (let i = 4; i < countyRow.length; i += 3) {
							let maxT = parseFloat(countyRow[i]);
							let upperT = parseFloat(countyRow[i + 1]);
							let lowerT = parseFloat(countyRow[i + 2]);

							if (measuring === 'veryHotDays') {
								maxT = Math.round(maxT);
								upperT = Math.round(upperT);
								lowerT = Math.round(lowerT);
							}

							maxTemps.push(maxT);
							upperBounds.push(upperT);
							lowerBounds.push(lowerT);
						}

						self.chartInstance = new Chart(ctx, {
							type: 'line',
							data: {
								labels: years,
								datasets: [
									{
										label: function (context) {
											switch (measuring) {
												case 'veryHotDays':
													return `Number of days with extreme heat in ${context[0].label}`;
												case 'avgDailyMaxTemp':
													return `Average daily maximum temperature in ${context[0].label}`;
												case 'totalPrecipitation':
													return `Total precipitation in ${context[0].label}`;
												default:
													return `Unknown measurement in ${context[0].label}`;
											}
										},
										data: maxTemps,
										borderColor: `${
											measuring === 'totalPrecipitation' ? '#253d86' : 'red'
										}`,
										backgroundColor: `${
											measuring === 'totalPrecipitation'
												? 'rgba(37, 61, 134, 0.95)'
												: 'rgba(255, 0, 0, 0.95)'
										}`,
										fill: false,
										tension: 0,
										pointRadius: self.chartPointRadius,
										pointHoverRadius: 7,
									},
									{
										label: 'Confidence Interval',
										data: lowerBounds,
										borderColor: 'rgba(255, 0, 0, 0)',
										backgroundColor: `${
											measuring === 'totalPrecipitation'
												? 'rgba(204, 213, 228, 0.3)'
												: 'rgba(255, 0, 0, 0.15)'
										}`,
										fill: '+1',
										pointRadius: 0,
										hoverRadius: 0,
										tension: 0,
									},
									{
										label: 'Confidence Interval',
										data: upperBounds,
										borderColor: 'rgba(255, 0, 0, 0)',
										backgroundColor: `${
											measuring === 'totalPrecipitation'
												? 'rgba(204, 213, 228, 0.3)'
												: 'rgba(255, 0, 0, 0.15)'
										}`,
										fill: '-1',
										pointRadius: 0,
										hoverRadius: 0,
										tension: 0,
									},
								],
							},
							options: {
								responsive: true,
								aspectRatio: window.innerWidth < 600 ? 1 : 1.61,
								scales: {
									y: {
										beginAtZero: false,
										min: Math.floor(minTemp),
										max: Math.ceil(maxTemp),
										title: {
											display: true,
											text:
												measuring === 'avgDailyMaxTemp'
													? 'Average daily maximum temperature (°F)'
													: measuring === 'totalPrecipitation'
													? 'Total precipitation (inches)'
													: 'Number of days with extreme heat',
											font: {
												weight: 700,
												//size: 16
												size: window.innerWidth < 700 ? 13 : 16,
											},
											color: '#202020',
										},
										ticks: {
											callback: function (value) {
												if (measuring === 'veryHotDays') {
													return `${Math.round(value)}`;
												} else if (measuring === 'totalPrecipitation') {
													return `${value}"`;
												}
												return `${value}°F`;
											},
										},
									},
									x: {
										title: {
											display: true,
											text: 'Year',
										},
									},
								},
								plugins: {
									legend: {
										display: false,
									},
									tooltip: {
										callbacks: {
											title: function (context) {
												switch (measuring) {
													case 'veryHotDays':
														return `Number of days with extreme heat in ${context[0].label}`;
													case 'avgDailyMaxTemp':
														return `Average daily maximum temperature in ${context[0].label}`;
													case 'totalPrecipitation':
														return `Total precipitation in ${context[0].label}`;
													default:
														return `Unknown measurement in ${context[0].label}`;
												}
											},
											label: function (context) {
												const avgTemp = context.parsed.y;

												switch (measuring) {
													case 'veryHotDays':
														return avgTemp;
													case 'totalPrecipitation':
														return `${avgTemp}"`;
													case 'avgDailyMaxTemp':
														return `${avgTemp}°F`;
													default:
														return avgTemp;
												}
											},
										},
									},
								},
							},
						});
					} else {
						const rows = csvText.split('\n').map((row) => row.split(','));
						const headers = rows[0];
						const data = rows.slice(1);

						const years = headers
							.filter((header) =>
								header.startsWith('yield_currentSystem_baseline_')
							)
							.map((header) =>
								parseInt(header.replace('yield_currentSystem_baseline_', ''))
							);

						const reverseProvinceMap = {
							Alberta: 'AB',
							Saskatchewan: 'SK',
						};

						const parts = self.selectedCountyInDropdown.split(',');
						const county = parts[0]?.trim();
						const provinceAbbr = parts[1]?.trim();

						const normalizedProvince =
							reverseProvinceMap[provinceAbbr] || provinceAbbr;
						const selectedCountyName = `${county}, ${normalizedProvince}`;

						const selectedCounty = data.find((row) => {
							let countyName = `${row[1].trim()}, ${row[2].trim()}`;

							if (self.country === 'ca') {
								if (countyName.startsWith('Division No ')) {
									countyName = countyName.replace(
										'Division No ',
										'Division No. '
									);
								}

								const csvProvinceAbbr =
									reverseProvinceMap[row[2].trim()] || row[2].trim();
								countyName = `${row[1].trim()}, ${csvProvinceAbbr}`;
							}

							return countyName === selectedCountyName;
						});

						if (!selectedCounty) {
							console.error(
								'Selected county not found in CSV data:',
								selectedCountyName
							);
							return;
						}

						const dataValues = selectedCounty.slice(4);

						const isAllValuesInvalid = dataValues.every(
							(val) => val === 'NA' || val === 'null' || parseFloat(val) === 0
						);

						if (isAllValuesInvalid) {
							self.chartErrorMessage = 'Insufficient data for this region';
							return;
						}

						const currentSystem = years.map((year) =>
							parseFloat(
								selectedCounty[
									headers.indexOf(`yield_currentSystem_baseline_${year}`)
								]
							).toFixed(2)
						);
						const techTrend = years.map((year) =>
							parseFloat(
								selectedCounty[
									headers.indexOf(`yield_technologyTrend_baseline_${year}`)
								]
							).toFixed(2)
						);
						const lowerBoundCurrent = years.map((year) =>
							parseFloat(
								selectedCounty[
									headers.indexOf(`yield_currentSystem_lower_${year}`)
								]
							).toFixed(2)
						);
						const upperBoundCurrent = years.map((year) =>
							parseFloat(
								selectedCounty[
									headers.indexOf(`yield_currentSystem_upper_${year}`)
								]
							).toFixed(2)
						);
						const lowerBoundTech = years.map((year) =>
							parseFloat(
								selectedCounty[
									headers.indexOf(`yield_technologyTrend_lower_${year}`)
								]
							).toFixed(2)
						);
						const upperBoundTech = years.map((year) =>
							parseFloat(
								selectedCounty[
									headers.indexOf(`yield_technologyTrend_upper_${year}`)
								]
							).toFixed(2)
						);

						let minYield = Math.min(...lowerBoundCurrent, ...lowerBoundTech);
						let maxYield = Math.max(...upperBoundCurrent, ...upperBoundTech);

						const padding = (maxYield - minYield) * 0.1;
						minYield = Math.floor(minYield - padding);
						maxYield = Math.ceil(maxYield + padding);

						minYield = Math.max(Number(minYield.toFixed(0)), 0);
						maxYield = Number(maxYield.toFixed(0));

						self.chartInstance = new Chart(ctx, {
							type: 'line',
							data: {
								labels: years,
								datasets: [
									{
										label: 'Current System',
										data: currentSystem,
										borderColor: 'rgba(51, 97, 85, 1)',
										backgroundColor: 'rgba(51, 97, 85, 0.9)',
										fill: false,
										tension: 0,
										pointHitRadius: 10,
										pointRadius: self.chartPointRadius,
									},
									{
										label: 'Confidence Interval',
										data: lowerBoundCurrent,
										borderColor: 'rgba(0, 0, 255, 0)',
										backgroundColor: 'rgba(51, 97, 85, 0.20)',
										fill: '+1',
										pointRadius: 0,
										hoverRadius: 0,
										tension: 0,
									},
									{
										label: 'Confidence Interval',
										data: upperBoundCurrent,
										borderColor: 'rgba(0, 0, 255, 0)',
										backgroundColor: 'rgba(51, 97, 85, 0.20)',
										fill: '-1',
										pointRadius: 0,
										hoverRadius: 0,
										tension: 0,
									},
									{
										label: 'Current System + Tech Trend',
										data: techTrend,
										borderColor: 'rgba(0, 138, 198, 1)',
										backgroundColor: 'rgba(0, 138, 198, 0.9)',
										fill: false,
										tension: 0,
										pointHitRadius: 10,
										pointRadius: self.chartPointRadius,
										pointStyle: 'rectRot',
									},
									{
										label: 'Confidence Interval',
										data: lowerBoundTech,
										borderColor: 'rgba(0, 255, 0, 0)',
										backgroundColor: 'rgba(0, 138, 198, 0.2)',
										fill: '+1',
										pointRadius: 0,
										hoverRadius: 0,
										tension: 0,
									},
									{
										label: 'Confidence Interval',
										data: upperBoundTech,
										borderColor: 'rgba(0, 255, 0, 0)',
										backgroundColor: 'rgba(0, 138, 198, 0.2)',
										fill: '-1',
										pointRadius: 0,
										hoverRadius: 0,
										tension: 0,
									},
								],
							},
							options: {
								responsive: true,
								aspectRatio: window.innerWidth < 600 ? 1 : 1.61,
								scales: {
									y: {
										beginAtZero: false,
										min: minYield,
										max: maxYield,
										title: {
											display: true,
											text: 'Average yield (bushels/acre)',
											font: {
												weight: 700,
												size: 16,
											},
											color: '#202020',
										},
									},
									x: {
										title: {
											display: true,
											text: 'Year',
										},
									},
								},
								plugins: {
									legend: {
										display: false,
									},
									tooltip: {
										callbacks: {
											title: function (context) {
												return `${self.measuring
													.charAt(0)
													.toUpperCase()}${self.measuring.slice(1)} in ${
													context[0].label
												}`;
											},
											label: function (context) {
												const cropYieldValue = context.parsed.y;
												return `${cropYieldValue} bushels`;
											},
										},
									},
								},
							},
						});
					}
				})
				.catch((error) => console.error('Error loading CSV data:', error));
		},
		generateProfitRiskChart(metric) {
			const self = this;
			const chartData = self.profitRiskData[metric];
			const ctx = document.getElementById(`chart-${metric}`);

			if (!ctx) {
				console.warn(`Chart container not found for ${metric}`);
				return;
			}

			self.destroyChart(`chart-${metric}`);

			if (!chartData || !chartData.years.length) {
				return;
			}

			const isInvalidData =
				chartData.values &&
				chartData.upperBounds &&
				chartData.lowerBounds &&
				chartData.values.every((v) => v === null) &&
				chartData.upperBounds.every((v) => v === null) &&
				chartData.lowerBounds.every((v) => v === null);

			if (isInvalidData) {
				self.chartErrorMessages[metric] = 'Insufficient data for this metric.';
				return;
			} else {
				self.chartErrorMessages[metric] = null;
			}

			const { years, values, upperBounds, lowerBounds } = chartData;

			const chartConfig = {
				revenuePerFarm: {
					label: `Total farm revenue (${
						self.currentFarm.country === 'us' ? 'USD' : 'CAD'
					})`,
					lineColor: 'rgba(37, 61, 134, 1)',
					backgroundColor: 'rgba(37, 61, 134, 0.15)',
					pointStyle: 'circle',
					unit: '$',
				},
				returnPerFarm: {
					label: `Total farm net return (${
						self.currentFarm.country === 'us' ? 'USD' : 'CAD'
					})`,
					lineColor: 'rgba(0, 142, 85, 1)',
					backgroundColor: 'rgba(0, 142, 85, 0.15)',
					pointStyle: 'rect',
					unit: '$',
				},
				debtToAssetRatio: {
					label: 'Ratio of debts relative to assets',
					lineColor: 'rgba(0, 138, 198, 1)',
					backgroundColor: 'rgba(0, 138, 198, 0.15)',
					pointStyle: 'rectRot',
					unit: '',
				},
			};

			const config = chartConfig[metric] || {
				label: metric,
				lineColor: '#888',
				backgroundColor: 'rgba(136, 136, 136, 0.2)',
			};

			self.chartInstances[metric] = new Chart(ctx, {
				type: 'line',
				data: {
					labels: years,
					datasets: [
						{
							label: config.label,
							data: values,
							borderColor: config.lineColor,
							backgroundColor: config.lineColor,
							fill: false,
							tension: 0,
							pointRadius: self.chartPointRadius,
							pointHoverRadius: self.chartPointRadius,
							pointStyle: config.pointStyle,
						},
						{
							label: 'Confidence Interval',
							data: lowerBounds,
							borderColor: 'rgba(0,0,0,0)',
							backgroundColor: config.backgroundColor,
							fill: '+1',
							pointRadius: 0,
							hoverRadius: 0,
							tension: 0,
						},
						{
							label: 'Confidence Interval',
							data: upperBounds,
							borderColor: 'rgba(0,0,0,0)',
							backgroundColor: config.backgroundColor,
							fill: '-1',
							pointRadius: 0,
							hoverRadius: 0,
							tension: 0,
						},
					],
				},
				options: {
					responsive: true,
					aspectRatio: window.innerWidth < 600 ? 1 : 1.61,
					scales: {
						y: {
							beginAtZero: true,
							title: {
								display: true,
								text: config.label,
								font: {
									weight: 700,
									size: 16,
								},
								color: '#202020',
							},
							ticks: {
								callback: function (value) {
									return `${config.unit}${self.formatNumber(value)}`;
								},
							},
							grid: {
								color: (context) =>
									context.tick.value === 0 ? '#808083' : '#d3d3d3',
								lineWidth: (context) => (context.tick.value === 0 ? 2 : 1),
							},
						},
						x: {
							title: {
								display: true,
								text: 'Years',
							},
						},
					},
					plugins: {
						legend: {
							display: false,
						},
						tooltip: {
							callbacks: {
								title: function (context) {
									const metric = config.label;
									const year = context[0].label;
									return `${metric} in ${year}`.trim();
								},
								label: function (context) {
									const value = self.formatNumber(context.parsed.y);

									if (config.unit) {
										return `${config.unit}${value}`;
									} else {
										return value;
									}
								},
							},
						},
					},
				},
			});
		},

		initializeRoute() {
			const self = this;
			const params = new URLSearchParams(window.location.search);
			const path = window.location.pathname.replace(/\/$/, '');

			self.currentYear = params.get('year')
				? parseInt(params.get('year'))
				: 2030;

			if (path.endsWith('/weather')) {
				self.currentPage = 'weather';
				self.measuring = params.get('measuring') || 'avgDailyMaxTemp';
				self.scope = params.get('scope') || 'us-and-ca';
				self.countyName = params.get('county') || '';
				self.stateAbbr = params.get('state') || '';
				self.country = params.get('country') || null;

				if (!self.country) {
					self.country = ['AB', 'SK'].includes(self.stateAbbr) ? 'ca' : 'us';
				}

				if (self.scope === 'county' && self.countyName && self.stateAbbr) {
					self.selectedCountyInDropdown = `${self.countyName}, ${self.stateAbbr}`;
				}
			} else if (path.endsWith('/crop-yield')) {
				self.currentPage = 'crop-yield';

				const defaultMeasurement = 'corn';
				const validMeasurements = Object.keys(
					self.measurementConfig['crop-yield']
				);
				const requestedMeasurement = params.get('measuring');

				self.measuring = validMeasurements.includes(requestedMeasurement)
					? requestedMeasurement
					: defaultMeasurement;

				if (['corn', 'soybeans', 'sorghum'].includes(self.measuring)) {
					self.scope = 'us';
				} else {
					self.scope = 'ca';
				}

				self.countyName = params.get('county') || '';
				self.stateAbbr = params.get('state') || '';
				self.country =
					params.get('country') || (self.scope === 'us' ? 'us' : 'ca');

				if (self.countyName && self.stateAbbr) {
					self.selectedCountyInDropdown = `${self.countyName}, ${self.stateAbbr}`;
					self.scope = 'county';
				}
			} else if (path.endsWith('/profit-risk')) {
				self.currentPage = 'profit-risk';

				if (params.has('customFarm')) {
					const location = params.get('location') || '';

					const isCanadianLocation = location
						.toLowerCase()
						.includes('division');

					self.customFarm.country = isCanadianLocation
						? 'ca'
						: params.get('country') || 'us';
					self.customFarm.location = location;
					self.customFarm.crop =
						params.get('crop') ||
						(isCanadianLocation ? 'Canola & wheat' : 'Corn & soybeans');
					self.customFarm.size = params.get('size') || 'Medium';
					self.customFarm.creditRisk = params.get('risk') || 'Low';
					self.currentFarm = { ...self.customFarm, id: 'custom' };
				} else if (params.has('exampleFarm')) {
					const sampleFarmId = params.get('exampleFarm');
					if (['one', 'two', 'three'].includes(sampleFarmId)) {
						self.selectFarm(sampleFarmId);
					}
				} else {
					self.selectFarm('one');
				}

				self.scope = null;
			} else if (path.endsWith('/next-steps')) {
				self.currentPage = 'next-steps';
				self.measuring = null;
				self.scope = null;
			} else {
				this.currentPage = 'home';
				this.measuring = null;
				this.scope = null;
				window.history.replaceState({}, '', baseUrl);
				return;
			}

			self.updateURL();
			self.fetchMeasurementData();
			self.makeAChart();
		},
		updateURL() {
			const params = new URLSearchParams();

			if (this.measuring) {
				params.set('measuring', this.measuring);
			}

			if (this.scope) {
				params.set('scope', this.scope || 'us-and-ca');
			}

			if (this.scope === 'county' && this.countyName && this.stateAbbr) {
				params.set('county', this.countyName);
				params.set('state', this.stateAbbr);
				params.set('country', this.country);
			}

			if (
				['weather', 'crop-yield'].includes(this.currentPage) &&
				this.scope !== 'county'
			) {
				params.set('year', this.currentYear);
			}

			if (this.currentPage === 'profit-risk') {
				if (['one', 'two', 'three'].includes(this.currentFarm.id)) {
					params.set('exampleFarm', this.currentFarm.id);
				} else {
					params.set('customFarm', '');
					params.set('location', this.customFarm.location || '');
					params.set('crop', this.customFarm.crop || '');
					params.set('size', this.customFarm.size || '');
					params.set('risk', this.customFarm.creditRisk || '');
				}
			} else {
				if (this.measuring) {
					params.set('measuring', this.measuring);
				}

				if (this.scope) {
					params.set('scope', this.scope || 'us-and-ca');
				}

				if (this.scope === 'county' && this.countyName && this.stateAbbr) {
					params.set('county', this.countyName);
					params.set('state', this.stateAbbr);
					params.set('country', this.country);
				}

				if (
					['weather', 'crop-yield'].includes(this.currentPage) &&
					this.scope !== 'county'
				) {
					params.set('year', this.currentYear);
				}
			}

			const newUrl = `${window.location.pathname}?${params.toString()}`;

			if (window.location.search !== `?${params.toString()}`) {
				window.history.pushState({}, '', newUrl);
			} else {
				window.history.replaceState({}, '', newUrl);
			}
		},
		async fetchMeasurementData() {
			const self = this;

			if (
				self.currentPage === 'home' ||
				self.currentPage === 'profit-risk' ||
				self.currentPage === 'next-steps'
			)
				return;

			if (
				!self.currentPage ||
				!self.measuring ||
				!self.measurementConfig[self.currentPage]?.[self.measuring]
			) {
				console.error(
					`Invalid measurement type: ${self.measuring} for page: ${self.currentPage}`
				);
				return;
			}

			const measurementData =
				self.measurementConfig[self.currentPage][self.measuring];
			const filePath = `${baseUrl}/assets/public/csv/${self.currentPage}/${measurementData.relativeCsv}`;

			try {
				const response = await fetch(filePath);
				const csvText = await response.text();
				const normalizedText = csvText.replace(/\r\n?/g, '\n');
				const rows = normalizedText.split('\n').map((row) => row.split(','));

				if (rows.length < 2) return;

				const headers = rows[0];

				const yearSuffix = `_${self.currentYear}`;
				const matchingColumn = headers.find((col) => col.endsWith(yearSuffix));

				if (!matchingColumn) {
					console.error(
						`Column with year suffix "${yearSuffix}" not found in CSV`
					);
					return;
				}

				const columnPrefix = matchingColumn.replace(yearSuffix, '');
				const selectedColumn = headers.indexOf(`${columnPrefix}${yearSuffix}`);

				if (selectedColumn === -1) {
					console.error(`Column ${columnPrefix}${yearSuffix} not found in CSV`);
					return;
				}

				let countyTempData = {};
				let allTempValues = [];

				rows.slice(1).forEach((row) => {
					const county = row[1]?.trim().replace(/\s+/g, '_');
					const state = row[2]?.trim();
					const id = `${county}_${state}`;

					const tempValue = parseFloat(row[selectedColumn]);
					if (!isNaN(tempValue)) {
						countyTempData[id] = tempValue;
					} else if (
						row[selectedColumn] === 'null' ||
						row[selectedColumn] === 'NA'
					) {
						countyTempData[id] = 'NA';
					}

					row.slice(4).forEach((value) => {
						const temp = parseFloat(value);
						if (!isNaN(temp)) {
							allTempValues.push(temp);
						}
					});
				});

				self.countyData = countyTempData;

				if (allTempValues.length > 0) {
					const maxAbsTemp = Math.max(...allTempValues.map(Math.abs));
					self.maxTemp = maxAbsTemp;
					self.minTemp = -maxAbsTemp;

					const thermometerSteps = measurementData.thermometerSteps;
					self.thermometerLabels = self.generateThermometerLabels(
						self.minTemp,
						self.maxTemp,
						thermometerSteps
					);
				}

				self.assignCountyColors(); // Update map colors
			} catch (error) {
				console.error('Error fetching measurement data:', error);
			}
		},
		generateThermometerLabels(min, max, steps) {
			const self = this;
			const unit =
				self.measurementConfig[self.currentPage]?.[self.measuring]?.unit || '';

			max = Math.ceil(max);
			min = Math.floor(min);

			let labels = [];
			labels.push(`${max}${unit}`);

			if (steps > 2) {
				const stepSize = (max - min) / (steps - 1);
				for (let i = 1; i < steps - 1; i++) {
					const value = max - i * stepSize;
					labels.push(`${Math.round(value)}${unit}`);
				}
			}

			labels.push(`${min}${unit}`);

			return labels;
		},
		async fetchProfitRiskData() {
			const self = this;
			const farm = self.currentFarm;

			if (!farm) {
				// No farm selected
				return;
			}

			const size = farm.size;

			const cropMap = {
				'Corn and soybeans': 'CornSoybean',
				'Corn & soybeans': 'CornSoybean',
				'Corn, soybeans, and sorghum': 'CornSoybeanSorghum',
				'Corn, soybeans and sorghum': 'CornSoybeanSorghum',
				'Corn, soybeans & sorghum': 'CornSoybeanSorghum',
				'Canola and wheat': 'CanolaWheat',
				'Canola & wheat': 'CanolaWheat',
				'Canola, wheat, and pea': 'CanolaWheatPea',
				'Canola, wheat and pea': 'CanolaWheatPea',
				'Canola, wheat & pea': 'CanolaWheatPea',

				Corn: 'Corn',
				Soybeans: 'Soybean',
				Sorghum: 'Sorghum',
				Canola: 'Canola',
				Wheat: 'Wheat',
				Peas: 'Peas',
			};

			const crop = cropMap[farm.crop] || farm.crop.replace(/\s+/g, '');
			const risk = farm.creditRisk;

			const filePaths = self.metrics.map((metric) => {
				const csvPath = self.getProfitRiskCsvPath(metric, size, crop, risk);
				return {
					metric,
					url: `${baseUrl}/assets/public/csv/${csvPath}`,
				};
			});

			try {
				const responses = await Promise.all(
					filePaths.map((fp) => fetch(fp.url))
				);

				const csvData = await Promise.all(
					responses.map((res, index) => {
						if (!res.ok) {
							console.error(
								`Failed to fetch ${filePaths[index].metric}: ${res.statusText}`
							);
							return null;
						}
						return res.text();
					})
				);

				self.profitRiskData = {};

				csvData.forEach((csvText, index) => {
					const metric = filePaths[index].metric;
					if (csvText) {
						const rows = csvText.split('\n').map((row) => row.split(','));
						const filteredData = self.filterCountyData(metric, rows);
						self.profitRiskData[metric] = filteredData;
					} else {
						console.warn(`No data available for ${metric}`);
					}
				});

				// Trigger chart rendering after data is loaded
				self.makeAChart();
			} catch (error) {
				console.error('Error fetching profit risk data:', error);
			}
		},
		getProfitRiskCsvPath(metric, size, crop, risk) {
			const self = this;
			const config = self.measurementConfig['profit-risk'][metric];
			if (!config.dynamicPath) return '';

			const fileName = config.fileTemplate
				.replace('{metric}', metric)
				.replace('{size}', size)
				.replace('{crop}', crop)
				.replace('{risk}', risk);

			return `${config.basePath}/${fileName}`;
		},
		filterCountyData(metric, rows) {
			const self = this;
			const countyName = self.currentFarm.location.split(',')[0].trim();
			const stateAbbr = self.currentFarm.location.split(',')[1].trim();

			if (!countyName || !stateAbbr) {
				console.warn(`Invalid county or state: ${countyName}, ${stateAbbr}`);
				return null;
			}

			const header = rows[0];
			const dataRow = rows.find(
				(row) => row[1]?.trim() === countyName && row[2]?.trim() === stateAbbr
			);

			if (!dataRow) {
				console.warn(
					`No data found for county ${countyName}, ${stateAbbr} in metric ${metric}`
				);
				return null;
			}

			const years = [];
			const values = [];
			const upperBounds = [];
			const lowerBounds = [];

			const seenYears = new Set();

			header.forEach((col, index) => {
				const columnName = col.trim();
				const yearMatch = columnName.match(/_(\d{4})$/);
				if (!yearMatch) return;

				const year = yearMatch[1];

				if (!seenYears.has(year)) {
					years.push(year);
					seenYears.add(year);
				}

				const cellValue = parseFloat(dataRow[index]);
				const parsedValue = isNaN(cellValue) ? null : cellValue;

				if (columnName.startsWith(metric)) {
					values.push(parsedValue);
				} else if (columnName.startsWith('upper')) {
					upperBounds.push(parsedValue);
				} else if (columnName.startsWith('lower')) {
					lowerBounds.push(parsedValue);
				}
			});

			return {
				metric,
				county: countyName,
				state: stateAbbr,
				years,
				values,
				upperBounds,
				lowerBounds,
			};
		},
		async fetchInsufficientData() {
			const self = this;

			const urlParams = new URLSearchParams(window.location.search);
			let category = urlParams.get('measuring') || 'weather';

			const categoryMapping = {
				avgDailyMaxTemp: 'weather',
				totalPrecipitation: 'weather',
				veryHotDays: 'weather',
				corn: 'cropYield',
				soybeans: 'cropYield',
				sorghum: 'cropYield',
				canola: 'cropYield',
				wheat: 'cropYield',
				pea: 'cropYield',
			};

			category = categoryMapping[category] || 'weather';

			self.currentCategory = category;

			const files = {
				weather: 'insufficientdata_weather.csv',
				cropYield: 'insufficientdata_crop-yield.csv',
				profitRisk: 'insufficientdata_profit-risk.csv',
			};

			const basePath = `${baseUrl}/assets/public/csv/insufficient/`;
			const filePaths = Object.fromEntries(
				Object.entries(files).map(([key, file]) => [key, `${basePath}${file}`])
			);

			try {
				const responses = await Promise.all(
					Object.values(filePaths).map((url) => fetch(url))
				);
				const csvData = await Promise.all(responses.map((res) => res.text()));

				self.insufficientLocations = {
					weather: self.parseInsufficientCSV(csvData[0]),
					cropYield: self.parseInsufficientCSV(csvData[1]),
					profitRisk: self.parseInsufficientCSV(csvData[2]),
				};
			} catch (error) {
				console.error('Error fetching insufficient data:', error);
			}
		},
		parseInsufficientCSV(csvText) {
			const rows = csvText.split('\n').map((row) => row.split(','));
			const locations = new Set();

			const stateAbbrMap = {
				Alberta: 'AB',
				Saskatchewan: 'SK',
				'South Dakota': 'SD',
				Nebraska: 'NE',
				Iowa: 'IA',
			};

			rows.slice(1).forEach((row) => {
				if (row.length < 4) return;

				let county = row[1].trim().replace(/\s+/g, ' ');
				let state = row[2].trim();
				let country = row[3].trim();

				county = county.replace(/Division No\.?\s*/, 'Division No. ').trim();

				if (stateAbbrMap[state]) {
					state = stateAbbrMap[state];
				}

				const key = `${county}, ${state}, ${country}`;
				locations.add(key);
			});

			return locations;
		},
		isLocationDisabled(county, state, country) {
			const self = this;

			if (!self.insufficientLocations || !self.currentCategory) {
				// Insufficient locations not loaded yet.
				return false;
			}

			county = county.trim().replace(/\s+/g, ' ');
			county = county.replace(/Division No\.?\s*/, 'Division No. ').trim();

			state = state.trim();
			country = country.trim();

			const stateAbbrMap = {
				Alberta: 'AB',
				Saskatchewan: 'SK',
				'South Dakota': 'SD',
				Nebraska: 'NE',
				Iowa: 'IA',
			};

			if (stateAbbrMap[state]) {
				state = stateAbbrMap[state];
			}

			const locationKey = `${county}, ${state}, ${country}`;

			return self.insufficientLocations[self.currentCategory]?.has(locationKey);
		},
	},
	watch: {
		currentYear() {
			this.updateURL();
			this.initializeRoute();
			this.fetchMeasurementData();
			this.assignCountyColors();
		},
		measuring() {
			this.updateURL();
			this.initializeRoute();
			this.fetchMeasurementData();
		},
		scope() {
			this.updateURL();
			this.initializeRoute();
			this.fetchMeasurementData();
			this.assignCountyColors();
		},
		currentPage() {
			this.updateURL();
			this.initializeRoute();
			this.fetchMeasurementData();
		},
		selectedCountyInDropdown() {
			this.chartErrorMessage = null;
			this.destroyChart();
		},
		'currentFarm.id'(newVal) {
			if (['one', 'two', 'three'].includes(newVal)) {
				this.selectFarm(newVal);
			} else if (newVal === 'custom') {
				this.currentFarm = { ...this.customFarm };
				this.fetchProfitRiskData().then(() => {});
			}
			this.updateURL();
		},
		customFarm: {
			handler() {
				if (this.currentFarm.id !== 'custom') {
					this.currentFarm = { ...this.customFarm, id: 'custom' };
				}
				this.fetchProfitRiskData().then(() => {
					this.makeAChart();
				});
				this.updateURL();
			},
			deep: true,
		},
	},
	computed: {
		isLargeFarmDisabled() {
			const self = this;
			const customFarmLocation = self.customFarm.location.split(',');
			const state = customFarmLocation.pop().trim();
			return state === 'SD' || state === 'IA';
		},
		chartPointRadius() {
			const width = window.innerWidth;
			if (width >= 1001) {
				return 6;
			} else if (width >= 650 && width <= 1000) {
				return 3;
			} else {
				return 0;
			}
		},
	},
	mounted() {
		const self = this;

		window.addEventListener('popstate', () => {
			self.initializeRoute();
		});

		self.initializeRoute();
		self.fetchMeasurementData();
		self.fetchInsufficientData();

		this.$nextTick(() => {
			self.updateRangeBackground();
		});

		setTimeout(() => {
			self.assignCountyColors();
			const rangeInput = document.querySelector(
				'.year-range-holder input[type="range"]'
			);
			if (rangeInput) {
				rangeInput.addEventListener('input', this.updateRangeBackground);
			}
		}, 300);
	},
});

app.mount('#app');
